import React, { useState, useEffect } from "react";
import { CustomButton } from "@/components/ui/CustomButton";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogPortal } from "@/components/ui/dialog";
import { format, addDays, addHours, startOfDay } from "date-fns";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

interface CallbackSlot {
  id: string;
  label: string;
  iso: string;
  disabled: boolean;
}

interface CallbackSlotSection {
  label: string;
  slots: CallbackSlot[];
}

const CALLBACK_SLOT_HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
const CALLBACK_SLOT_HOURS_AHEAD = 48;

interface CallBackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (nextCallAt: string) => Promise<boolean>;
  updating?: boolean;
}

export const CallBackModal: React.FC<CallBackModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  updating = false,
}) => {
  const [callbackSlotSections, setCallbackSlotSections] = useState<CallbackSlotSection[]>([]);
  const [selectedCallbackSlot, setSelectedCallbackSlot] = useState<string | null>(null);

  // Debug: log when modal opens unexpectedly
  useEffect(() => {
    if (open) {
      console.log('[CallBackModal] Modal opened - open prop is true');
      // Log stack trace to see what triggered it
      console.trace('[CallBackModal] Stack trace');
    }
  }, [open]);

  const buildCallbackSections = (): CallbackSlotSection[] => {
    const now = new Date();
    const limit = addHours(now, CALLBACK_SLOT_HOURS_AHEAD);

    return [0, 1, 2].map((offset) => {
      const dayStart = startOfDay(addDays(now, offset));
      const label =
        offset === 0
          ? "Today"
          : offset === 1
          ? "Tomorrow"
          : format(dayStart, "EEEE");

      const slots = CALLBACK_SLOT_HOURS.map((hour) => {
        const slotDate = new Date(dayStart);
        slotDate.setHours(hour, 0, 0, 0);
        const iso = slotDate.toISOString();
        const disabled = slotDate <= now || slotDate > limit;
        return {
          id: `${offset}-${hour}`,
          label: format(slotDate, "hh:mm a"),
          iso,
          disabled,
        };
      });

      return { label, slots };
    });
  };

  useEffect(() => {
    if (open) {
      const sections = buildCallbackSections();
      setCallbackSlotSections(sections);
      // Do not auto-select a time; user can save with or without a time (optional)
      setSelectedCallbackSlot(null);
    }
  }, [open]);

  const handleClose = () => {
    setSelectedCallbackSlot(null);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    // Time is optional: pass selected slot or empty string (backend uses 48h vs 12h unassign accordingly)
    const ok = await onSubmit(selectedCallbackSlot ?? "");
    if (ok) {
      handleClose();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogPortal>
        {/* No overlay - modal slides in from right without blocking main content */}
        <DialogPrimitive.Content
          onWheel={(e) => e.stopPropagation()}
          className={cn(
            // inset-y-0 sizes to the visible viewport; avoid h-screen (pushes footer below fold on tablets)
            "fixed inset-y-0 right-0 left-auto top-0 z-[200] flex h-full max-h-[100dvh] w-full max-w-[320px] flex-col overflow-hidden",
            "rounded-none border-l border-[#ded8d2] bg-[#f9f6f2] p-0 shadow-2xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            "sm:w-[320px]"
          )}
          aria-describedby="callback-dialog-description"
          style={{ zIndex: 200 }}
        >
          <div className="flex h-full min-h-0 flex-col">
            <div className="flex flex-shrink-0 items-center justify-between border-b border-[#e7e1db] px-4 py-4">
              <div className="space-y-1 pr-2">
                <DialogPrimitive.Title className="text-[20px] font-semibold text-black">
                  Select time
                </DialogPrimitive.Title>
                <DialogPrimitive.Description
                  id="callback-dialog-description"
                  className="text-sm text-[#8c8176]"
                >
                  Optionally pick a time within the next 48 hours, or save without a time.
                </DialogPrimitive.Description>
              </div>
              <DialogPrimitive.Close className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            </div>

            <div
              className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-4 py-4"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {callbackSlotSections.map((section) => (
                <div key={section.label} className="space-y-3">
                  <p className="text-sm font-semibold text-[#9b8f84]">{section.label}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {section.slots.map((slot) => {
                      const isSelected = selectedCallbackSlot === slot.iso;
                      return (
                        <button
                          key={slot.id}
                          type="button"
                          disabled={slot.disabled}
                          onClick={() => setSelectedCallbackSlot(slot.iso)}
                          className={cn(
                            "rounded-full px-4 py-2 text-sm font-medium transition shadow-inner",
                            slot.disabled
                              ? "cursor-not-allowed bg-[#e8e3dd] text-[#b7aea4]"
                              : isSelected
                              ? "bg-black text-white shadow-md"
                              : "bg-[#efe8e1] text-[#6d5f54] hover:bg-[#e7dfd7]"
                          )}
                        >
                          {slot.label.toLowerCase()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Pinned footer — always visible on tablet/desktop/mobile */}
            <div className="flex-shrink-0 border-t border-[#e7e1db] bg-[#f4efe9] px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <CustomButton
                  variant="ghost"
                  className="w-full sm:w-auto rounded-full border border-transparent text-black hover:border-black hover:bg-transparent"
                  onClick={handleClose}
                >
                  Close
                </CustomButton>
                <CustomButton
                  className="w-full sm:w-auto rounded-full bg-black text-white hover:bg-black/90"
                  onClick={handleSubmit}
                  disabled={updating}
                  loading={updating}
                >
                  Save
                </CustomButton>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
